/**
 * RHA Safety Inspection & Maintenance Record form definitions.
 *
 * Transcribed verbatim from the printed pads:
 *   VEH001 - Safety Inspection and Maintenance Record Forms for Powered Vehicles
 *            (Revised November 2025)
 *   VEH002 - Safety Inspection and Maintenance Record Forms for Trailers and Semi-Trailers
 *            (Revised June 2023)
 *
 * Published by The Road Haulage Association Limited, Roadway House, Bretton Way,
 * Bretton, Peterborough PE3 8DD.
 *
 * Item keys are STABLE. Never renumber them - completed inspections reference them
 * forever. To retire an item, mark it `retired: true`; to add one, append a new key.
 */

/** Marking code (MC) as printed on the form header. */
export type MarkCode = 'S' | 'X' | 'R' | 'NA' | 'M';

export const MARK_CODES: Record<MarkCode, { symbol: string; label: string; hint?: string }> = {
  S: { symbol: '✔', label: 'Satisfactory' },
  X: { symbol: 'X', label: 'Safety item defect' },
  R: { symbol: 'R', label: 'Repair required' },
  NA: { symbol: 'N/A', label: 'Not applicable' },
  M: { symbol: 'M', label: 'Monitor', hint: 'Possible maintenance required before next service' },
};

/** Codes that put the asset into a defect state and require rectification before sign-off. */
export const DEFECT_CODES: MarkCode[] = ['X', 'R'];

export interface InspectionItem {
  /** Stable identifier, e.g. "IV03". Persisted against every inspection. */
  key: string;
  /** Label exactly as printed on the form. */
  label: string;
  /** DVSA HGV Inspection Manual item number, as printed. Blank on the form where absent. */
  im?: string;
  /** Set when the printed IM number could not be read with confidence from the pad. */
  imUnverified?: boolean;
  retired?: boolean;
}

export interface InspectionSection {
  key: string;
  title: string;
  /** Which printed column the section sits in - mirrors the paper layout on wide screens. */
  column: 'left' | 'right';
  items: InspectionItem[];
}

export type FormCode = 'VEH001' | 'VEH002';

export interface FormDef {
  code: FormCode;
  title: string;
  subtitle: string;
  revision: string;
  /** Which asset kind this form applies to. */
  appliesTo: 'vehicle' | 'trailer';
  sections: InspectionSection[];
  /** IM references used by the braking performance assessment table on page 2. */
  brakeRefs: { checkNo?: string; im: string; label: string }[];
  /** Header fields printed at the top of page 1. */
  headerFields: string[];
  /** Extra page-2 blocks this variant carries. */
  hasTachoDownload: boolean;
  hasTyreAgeGrid: boolean;
}

/* ------------------------------------------------------------------ *
 * VEH001 - Powered Vehicles
 * ------------------------------------------------------------------ */

const VEH001_INSIDE: InspectionItem[] = [
  { key: 'IV01', label: 'DfT plate - condition details' },
  { key: 'IV02', label: 'Speed limiter plate - condition details', im: '33' },
  { key: 'IV03', label: 'Seat belts and supplementary restraint systems', im: '3' },
  { key: 'IV04', label: 'Cab floor and steps', im: '17' },
  { key: 'IV05', label: 'Seats', im: '18' },
  { key: 'IV06', label: 'Other seats and crew amenities' },
  { key: 'IV07', label: 'Mirrors and direct vision devices', im: '22' },
  { key: 'IV08', label: 'Glass and view of the road', im: '23' },
  { key: 'IV09', label: 'Windscreen washers and wipers', im: '25' },
  { key: 'IV10', label: 'Speedometer/tachograph - operation - seals - plaque detail', im: '26' },
  { key: 'IV11', label: 'Engine tachometer' },
  { key: 'IV12', label: 'Audible warning - horn', im: '27' },
  { key: 'IV13', label: 'Driving controls/warning lamps (inc ADAS)', im: '28' },
  { key: 'IV14', label: 'Steering control', im: '30' },
  { key: 'IV15', label: 'Anti-theft locks' },
  { key: 'IV16', label: 'Pressure/vacuum warning and build-up', im: '34' },
  { key: 'IV17', label: 'Other gauges - warning devices' },
  { key: 'IV18', label: 'Hand lever controlling mechanical & electronic park brake', im: '36' },
  { key: 'IV19', label: 'Service brake pedal', im: '37' },
  { key: 'IV20', label: 'Service brake operation (inc ABS/EBS, ESC & ISO 7638 cable)', im: '38' },
  { key: 'IV21', label: 'Hand operated brake control valves', im: '39' },
  { key: 'IV22', label: 'Electrical wiring/equipment/switches', im: '42' },
  { key: 'IV23', label: 'Front fog and aux lamp switches' },
  { key: 'IV24', label: 'Panel/interior lamps and switches' },
  { key: 'IV25', label: 'Cab heater/demister/air conditioning' },
];

const VEH001_ENGINE: InspectionItem[] = [
  { key: 'EC01', label: 'Engine & Transmission mountings', im: '43' },
  { key: 'EC02', label: 'Oil leaks', im: '44' },
  { key: 'EC03', label: 'Fuel tanks and systems', im: '45' },
  { key: 'EC04', label: 'Exhaust Systems and Nuisance', im: '46' },
  { key: 'EC05', label: 'Exhaust brake' },
  { key: 'EC06', label: 'Radiator mounting' },
  { key: 'EC07', label: 'Cooling system' },
  { key: 'EC08', label: 'Fan, generator, aux belts' },
  { key: 'EC09', label: 'Fuel pump linkage seals and EDC equipment' },
  { key: 'EC10', label: 'Speed limiter - condition-seals-linkage', im: '33' },
  { key: 'EC11', label: 'Injectors, pipes, filters' },
  { key: 'EC12', label: 'Air intake system - turbocharger intercooler and filters' },
  { key: 'EC13', label: 'Air compressor - exhauster - drive belts (if applicable)', im: '59' },
];

const VEH001_GROUND: InspectionItem[] = [
  { key: 'GL01', label: 'Road wheels and hubs', im: '6' },
  { key: 'GL02', label: 'Cab security', im: '15' },
  { key: 'GL03', label: 'Cab doors', im: '16' },
  { key: 'GL04', label: 'Sideguards, rear under-run and bumper bars', im: '9' },
  { key: 'GL05', label: 'Spare wheel and carrier', im: '10' },
  { key: 'GL06', label: 'Spray suppression, wings and wheel arches', im: '14' },
  { key: 'GL07', label: 'Security/condition of body, containers & crane support legs', im: '19/20' },
  { key: 'GL08', label: 'Demountable bodies - chassis mounted equipment' },
  { key: 'GL09', label: 'Chassis - condition', im: '41' },
  { key: 'GL10', label: 'Electrical wiring equipment including batteries', im: '42' },
  { key: 'GL11', label: 'Electrical connections for trailer', im: '42' },
  { key: 'GL12', label: 'Suspension', im: '48' },
  { key: 'GL13', label: 'Axles, stub axles and wheel bearings', im: '53' },
  { key: 'GL14', label: 'Steering', im: '54' },
  { key: 'GL15', label: 'Transmission', im: '57' },
  { key: 'GL16', label: 'Clutch operation' },
  { key: 'GL17', label: 'Gearbox and bell housing' },
  { key: 'GL18', label: 'Markers and reflectors', im: '62' },
  { key: 'GL19', label: 'Lamps', im: '63' },
  { key: 'GL20', label: 'Direction indicators & hazard warning lamps', im: '66' },
  { key: 'GL21', label: 'Aim of Headlamps', im: '67' },
  {
    key: 'GL22',
    label:
      'Registration plates - secure, legal, complete and legible. Reference to BS AU 145e code for plates fitted after 2021',
    im: '1',
  },
  { key: 'GL23', label: 'Other dangerous defects', im: '74' },
  { key: 'GL24', label: 'Exhaust emissions MIL', im: '5' },
  { key: 'GL25', label: 'Safety Recall Check' },
  { key: 'GL26', label: 'OBD Diagnostics Trouble Code (DTC) Check' },
  { key: 'GL27', label: 'Vehicle to trailer coupling', im: '11' },
];

const VEH001_BRAKES: InspectionItem[] = [
  { key: 'BR01', label: 'Brake system and components', im: '59' },
  { key: 'BR02', label: 'Trailer coupling hoses & function of self sealing valves', im: '12' },
  { key: 'BR03', label: 'Air brake anti-freeze device' },
  { key: 'BR04', label: 'Anti-lock device - operation warning', im: '38' },
  { key: 'BR05', label: 'Operation of multi-circuit protection' },
  { key: 'BR06', label: 'Additional braking devices', im: '58' },
];

const VEH001_TYRES: InspectionItem[] = [
  {
    key: 'TY01',
    label: 'Size and type of tyres, Drive axle tyres comply with calibration plaque',
    im: '7',
    imUnverified: true,
  },
  {
    key: 'TY02',
    label: 'Condition of Tyres (including age & date code)',
    im: '8',
    imUnverified: true,
  },
];

export const VEH001: FormDef = {
  code: 'VEH001',
  title: 'Safety Inspection and Maintenance',
  subtitle: 'Record Forms for Powered Vehicles',
  revision: 'Revised November 2025',
  appliesTo: 'vehicle',
  headerFields: [
    'Operator',
    'Inspection Organisation',
    'Reg no',
    'Location of Inspection',
    'Make/Model',
    'VIN no',
    'Odo reading',
    'O-Licence & Expiry Date',
    'ISO Week Number',
    'Reg/Fleet no',
    'Tachograph Calibration date',
    'Date in for Inspection',
    'Last 2 year Calibration date',
  ],
  sections: [
    { key: 'IV', title: 'Inside Vehicle', column: 'left', items: VEH001_INSIDE },
    { key: 'EC', title: 'Engine Compartment', column: 'left', items: VEH001_ENGINE },
    { key: 'GL', title: 'Ground Level and Under Vehicle', column: 'right', items: VEH001_GROUND },
    { key: 'BR', title: 'Brakes', column: 'right', items: VEH001_BRAKES },
    { key: 'TY', title: 'Tyres', column: 'right', items: VEH001_TYRES },
  ],
  brakeRefs: [
    { im: '71', label: 'Service Brake Performance' },
    { im: '72', label: 'Secondary Brake Performance' },
    { im: '73', label: 'Parking Brake Performance' },
  ],
  hasTachoDownload: true,
  hasTyreAgeGrid: true,
};

/* ------------------------------------------------------------------ *
 * VEH002 - Trailers and Semi-Trailers
 * ------------------------------------------------------------------ */

const VEH002_UNDER: InspectionItem[] = [
  { key: 'UT01', label: 'DfT Plate - position detail' },
  { key: 'UT02', label: 'Bumpers - security-condition-dimension', im: '9' },
  { key: 'UT03', label: 'Rear underrun device - security-condition-dimension', im: '9' },
  { key: 'UT04', label: 'Protective sideguards - security-condition-dimension', im: '9' },
  { key: 'UT05', label: 'Spare wheel carrier - security/condition-wheel attachment', im: '10' },
  { key: 'UT06', label: 'Vehicle to trailer coupling', im: '11' },
  { key: 'UT07', label: 'Fifth wheel king pin and rubbing plate', im: '11' },
  { key: 'UT08', label: 'Auto coupling fore-carriage - condition-security-locking devices', im: '11' },
  { key: 'UT09', label: 'Drawbar including attachment - condition-security-locking devices', im: '11' },
  { key: 'UT10', label: 'Trailer landing legs - condition-security', im: '13' },
  { key: 'UT11', label: 'Condition of Body', im: '20' },
  { key: 'UT12', label: 'Spray Suppression, Wings and Wheel Arches', im: '14' },
  { key: 'UT13', label: 'Security of body containers and crane support legs', im: '19' },
  { key: 'UT14', label: 'Oil leaks - extent', im: '44' },
  {
    key: 'UT15',
    label: 'Fuel tanks and systems - security-leaks-damage, including filler cap',
    im: '45',
  },
  { key: 'UT16', label: 'Condition of Chassis', im: '41' },
  {
    key: 'UT17',
    label: 'Electrical wiring, coupling sockets and equipment - security-condition',
    im: '42',
  },
];

const VEH002_SUSPENSION: InspectionItem[] = [
  { key: 'SU01', label: 'Suspension', im: '48' },
  { key: 'SU02', label: 'Suspension pins bushes', im: '48' },
  { key: 'SU03', label: 'Suspension springs and links - condition', im: '48' },
  { key: 'SU04', label: 'Spring units links and sub-frames - attachment', im: '48' },
  { key: 'SU05', label: 'Shock absorbers (dampers)', im: '48' },
  { key: 'SU06', label: 'Axle lift devices' },
  { key: 'SU07', label: 'Road Wheels and hubs - condition-security', im: '6' },
  { key: 'SU08', label: 'Rear axle wheel bearings and seals' },
  { key: 'SU09', label: 'Transmission', im: '57' },
];

const VEH002_STEERING: InspectionItem[] = [
  { key: 'ST01', label: 'Axles, Stub Axles and Wheel Bearings', im: '53' },
  {
    key: 'ST02',
    label: 'Steering mechanisms-wear-security-condition-adjustment-Dust Covers',
    im: '54',
  },
  { key: 'ST03', label: 'Vehicle to Trailer Coupling', im: '11' },
  { key: 'ST04', label: 'Steering alignment' },
];

const VEH002_BRAKES: InspectionItem[] = [
  { key: 'TB01', label: 'Brake Systems and Components', im: '59' },
  { key: 'TB02', label: 'Trailer Parking and Emergency Brake and Air Line Connections', im: '12' },
  {
    key: 'TB03',
    label: 'Brake actuators and adjusters - security-condition-adjustment-leaks',
    im: '59',
  },
  {
    key: 'TB04',
    label: 'Braking system and components - security-condition-adjustment-leaks',
    im: '59',
  },
  { key: 'TB05', label: 'Service Brake Operation (inc ABS/EBS, ESC & ISO 7638 cable)', im: '38' },
  {
    key: 'TB06',
    label: 'Load sensing equipment - security-condition-adjustment-leaks',
    im: '59',
  },
  { key: 'TB07', label: 'Parking brake - function-leaks-compliance', im: '12' },
  { key: 'TB08', label: 'Emergency brake - function-leaks-compliance', im: '12' },
  { key: 'TB09', label: 'Operating adaptor (service line)', im: '12' },
  { key: 'TB10', label: 'Hand operated brake control valves - function-condition', im: '39' },
  { key: 'TB11', label: 'Additional braking devices', im: '58' },
];

const VEH002_MARKINGS: InspectionItem[] = [
  { key: 'ML01', label: 'Markers and reflectors', im: '62' },
  {
    key: 'ML02',
    label: 'Rear markings (reflective) - security-condition-position-specification',
    im: '62',
  },
  {
    key: 'ML03',
    label: 'Conspicuity markings - security-condition-position-specification',
    im: '62',
  },
  { key: 'ML04', label: 'Lamps', im: '63' },
  { key: 'ML05', label: 'Front lamps and outline markers - position-operation-specification', im: '63' },
  { key: 'ML06', label: 'Rear lamps and outline markers - position-operation-specification', im: '63' },
  { key: 'ML07', label: 'Number plate lamp(s) - position-operator-specification', im: '63' },
  { key: 'ML08', label: 'Rear fog lamps - position-operator-specification', im: '63' },
  {
    key: 'ML09',
    label: 'Reflectors (front, rear, side) - security-condition-position-specification',
    im: '62',
  },
  { key: 'ML10', label: 'Direction/hazard indicators - position-operation-specification', im: '66' },
  { key: 'ML11', label: 'Stop lamps - position-operation-specification', im: '63' },
  { key: 'ML12', label: 'Reversing lamp(s)', im: '63' },
  { key: 'ML13', label: 'Other lamps' },
  { key: 'ML14', label: 'Hazard warning signs' },
  { key: 'ML15', label: 'Side marker lamps', im: '63' },
  { key: 'ML16', label: 'Paintwork and livery' },
  { key: 'ML17', label: 'Number plate carrier' },
  { key: 'ML18', label: 'Other dangerous defects', im: '74' },
];

const VEH002_TYRES: InspectionItem[] = [
  { key: 'TT01', label: 'Size and type of tyres', im: '7' },
  { key: 'TT02', label: 'Condition of Tyres (including age & date code)', im: '8' },
];

export const VEH002: FormDef = {
  code: 'VEH002',
  title: 'Safety Inspection and Maintenance Record',
  subtitle: 'Forms for Trailers and Semi-Trailers',
  revision: 'Revised June 2023',
  appliesTo: 'trailer',
  headerFields: [
    'Operator',
    'Inspection Organisation',
    'Chassis no',
    'Location of Inspection',
    'Make',
    'Model',
    'Body type',
    'Hubometer',
    'ISO Week Number',
    'Inspection date',
    'Fleet no',
    'VIN no',
  ],
  sections: [
    { key: 'UT', title: 'Under/Alongside Trailer', column: 'left', items: VEH002_UNDER },
    { key: 'SU', title: 'Suspension', column: 'left', items: VEH002_SUSPENSION },
    {
      key: 'ST',
      title: 'Steering - Rear Steer & Drawbar Trailers',
      column: 'left',
      items: VEH002_STEERING,
    },
    { key: 'TB', title: 'Brakes', column: 'right', items: VEH002_BRAKES },
    { key: 'ML', title: 'Markings, Lamps and Reflectors', column: 'right', items: VEH002_MARKINGS },
    { key: 'TT', title: 'Tyres', column: 'right', items: VEH002_TYRES },
  ],
  brakeRefs: [
    { checkNo: '52', im: '71', label: 'Service Brake Performance' },
    { checkNo: '53', im: '72', label: 'Secondary Brake Performance' },
    { checkNo: '54', im: '73', label: 'Parking Brake Performance' },
  ],
  hasTachoDownload: false,
  hasTyreAgeGrid: false,
};

export const FORMS: Record<FormCode, FormDef> = { VEH001, VEH002 };

export function formForAssetKind(kind: 'vehicle' | 'trailer'): FormDef {
  return kind === 'trailer' ? VEH002 : VEH001;
}

export function allItems(form: FormDef): InspectionItem[] {
  return form.sections.flatMap((s) => s.items);
}

export function itemCount(form: FormDef): number {
  return allItems(form).length;
}

/** Flat lookup of every item across both forms, for rendering historic records. */
export const ITEM_INDEX: Record<
  string,
  { item: InspectionItem; section: string; form: FormCode }
> = Object.values(FORMS).reduce(
  (acc, form) => {
    for (const section of form.sections) {
      for (const item of section.items) {
        acc[item.key] = { item, section: section.title, form: form.code };
      }
    }
    return acc;
  },
  {} as Record<string, { item: InspectionItem; section: string; form: FormCode }>,
);
